import { useEffect, useState, useRef } from 'react';
import { useMsal } from '@azure/msal-react';
import { Button, Card, CardContent, CardHeader, Divider, Grid, Stack, Typography, Popover } from '@mui/material';
import { RegulationService, getAuthToken } from '../../service';
import { Lookups } from '../../utils';

import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import DirectionsBoatIcon from '@mui/icons-material/DirectionsBoat';
import FlagIcon from '@mui/icons-material/Flag';
import SouthAmericaIcon from '@mui/icons-material/SouthAmerica';
import portService from '../../service/port.service';
import ArrowLeftIcon from '@mui/icons-material/ArrowLeft';
import InfoIcon from '@mui/icons-material/Info';

const Header = ({ locationPin, locationPinOption }) => {
    const getAvatar = () => {
        if (locationPinOption === Lookups.MapRegulatoryGroup.Port) {
            return <DirectionsBoatIcon />;
        } else if (locationPinOption === Lookups.MapRegulatoryGroup.State) {
            return <AccountBalanceIcon />;
        } else if (locationPinOption === Lookups.MapRegulatoryGroup.Country) {
            return <FlagIcon />;
        } else if (locationPinOption === Lookups.MapRegulatoryGroup.Region) {
            return <SouthAmericaIcon />;
        }
    };

    const getTitle = () => {
        return <Typography>{`${locationPin.name}`}</Typography>;
    };

    const getSubHeader = () => {
        return (
            <Typography variant="caption" display="block">
                {locationPinOption === Lookups.MapRegulatoryGroup.Port && `${locationPin.country}, ${locationPin.region}`}
                {locationPinOption === Lookups.MapRegulatoryGroup.State && `${locationPin.country}, ${locationPin.region}`}
                {locationPinOption === Lookups.MapRegulatoryGroup.Country && `${locationPin.region}`}
            </Typography>
        );
    };

    return <CardHeader avatar={getAvatar()} title={getTitle()} subheader={getSubHeader()} style={{ padding: 0 }} />;
};

const Content = ({ summaryData, OnSeeMore }) => {
    return (
        <CardContent style={{ padding: 0 }}>
            <Stack direction={'column'} spacing={1} alignItems={'stretch'} justifyContent={'flex-start'}>
                <Grid container spacing={0.1}>
                    {/* Regulatory */}
                    <Grid item xs={12} style={{ textAlign: 'left' }}>
                        <Typography>
                            <strong>Regulatory</strong>
                        </Typography>
                    </Grid>
                    {/* Region */}
                    <Grid item xs={1} style={{ textAlign: 'right' }}>
                        <Typography variant="subtitle2">{`${summaryData.Regulation.Region}`}&nbsp;</Typography>
                    </Grid>
                    <Grid item xs={11} style={{ textAlign: 'left' }}>
                        <Typography variant="subtitle2">&nbsp;Region Requirements</Typography>
                    </Grid>
                    {/* Country */}
                    <Grid item xs={1} style={{ textAlign: 'right' }}>
                        <Typography variant="subtitle2">{`${summaryData.Regulation.Country}`}&nbsp;</Typography>
                    </Grid>
                    <Grid item xs={11} style={{ textAlign: 'left' }}>
                        <Typography variant="subtitle2">&nbsp;Country Requirements</Typography>
                    </Grid>
                    {/* State */}
                    <Grid item xs={1} style={{ textAlign: 'right' }}>
                        <Typography variant="subtitle2">{`${summaryData.Regulation.State}`}&nbsp;</Typography>
                    </Grid>
                    <Grid item xs={11} style={{ textAlign: 'left' }}>
                        <Typography variant="subtitle2">&nbsp;State Requirements</Typography>
                    </Grid>
                    {/* Port */}
                    <Grid item xs={1} style={{ textAlign: 'right' }}>
                        <Typography variant="subtitle2">{`${summaryData.Regulation.Port}`}&nbsp;</Typography>
                    </Grid>
                    <Grid item xs={11} style={{ textAlign: 'left' }}>
                        <Typography variant="subtitle2">&nbsp;Port Requirements</Typography>
                    </Grid>
                    {/* Incentives */}
                    <Grid item xs={12} style={{ textAlign: 'left' }}>
                        <Typography>
                            <strong>Incentives</strong>
                        </Typography>
                    </Grid>
                    {/* Port Incentives */}
                    <Grid item xs={1} style={{ textAlign: 'right' }}>
                        <Typography variant="subtitle2">{`${summaryData.Incentive.Port}`}&nbsp;</Typography>
                    </Grid>
                    <Grid item xs={11} style={{ textAlign: 'left' }}>
                        <Typography variant="subtitle2">&nbsp;Port Incentives</Typography>
                    </Grid>
                    {/* Alt. Fuels */}
                    <Grid item xs={12} style={{ textAlign: 'left' }}>
                        <Typography>
                            <strong>Alt. Fuels</strong>
                        </Typography>
                    </Grid>
                    <Grid item xs={1} style={{ textAlign: 'right' }}>
                        <Typography variant="subtitle2">{`${summaryData.Fuel.Country}`}&nbsp;</Typography>
                    </Grid>
                    <Grid item xs={11} style={{ textAlign: 'left' }}>
                        <Typography variant="subtitle2">&nbsp;Alt. Fuel Facilities</Typography>
                    </Grid>
                </Grid>

                <Divider sx={{ border: '1px solid #da1f33' }} orientation="horizontal" flexItem />
                <Button size="small" variant="contained" onClick={OnSeeMore}>
                    See More
                </Button>
                <Button size="small" variant="outlined" href="https://abs.jotform.com/222194343602954" target="_blank">
                    Give Feedback
                </Button>
            </Stack>
        </CardContent>
    );
};

const PortInformationDisplay = ({ portInformation, xlength, ylength }) => {
    return (
        <>
            <Card
                sx={{
                    width: xlength,
                    height: ylength,
                    overflowY: 'auto',
                    overflowX: 'auto',
                    boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.1)'
                }}
                elevation={0}
            >
                <Typography style={{ marginLeft: '10px' }} variant="h7" gutterBottom bold>
                    <strong>Port Information</strong>
                </Typography>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <tbody>
                        <tr>
                            <td style={{ textAlign: 'left', width: '30%' }}>
                                <Typography style={{ marginLeft: '15px' }} variant="subtitle2">
                                    Port Name
                                </Typography>
                            </td>
                            <td style={{ textAlign: 'left', width: '70%' }}>
                                <Typography style={{ marginLeft: '15px' }} variant="subtitle2">
                                    {portInformation.name}
                                </Typography>
                            </td>
                        </tr>
                        <tr>
                            <td style={{ textAlign: 'left', width: '30%' }}>
                                <Typography style={{ marginLeft: '15px' }} variant="subtitle2">
                                    UN/LOCODE
                                </Typography>
                            </td>
                            <td style={{ textAlign: 'left', width: '70%' }}>
                                <Typography style={{ marginLeft: '15px' }} variant="subtitle2">
                                    {portInformation.unlocode || '-'}
                                </Typography>
                            </td>
                        </tr>
                        <tr>
                            <td style={{ textAlign: 'left', width: '30%' }}>
                                <Typography variant="subtitle2" style={{ marginLeft: '15px' }}>
                                    Website
                                </Typography>
                            </td>
                            <td style={{ textAlign: 'left', width: '70%' }}>
                                <Typography style={{ marginLeft: '15px' }} variant="subtitle2">
                                    {portInformation.website || '-'}
                                </Typography>
                            </td>
                        </tr>
                        <tr>
                            <td style={{ textAlign: 'left', width: '30%' }}>
                                <Typography variant="subtitle2" style={{ marginLeft: '15px' }}>
                                    Phone
                                </Typography>
                            </td>
                            <td style={{ textAlign: 'left', width: '70%' }}>
                                <Typography style={{ marginLeft: '15px' }} variant="subtitle2">
                                    {portInformation.phone || '-'}
                                </Typography>
                            </td>
                        </tr>
                        <tr>
                            <td style={{ textAlign: 'left', width: '30%' }}>
                                <Typography variant="subtitle2" style={{ marginLeft: '15px' }}>
                                    Email
                                </Typography>
                            </td>
                            <td style={{ textAlign: 'left', width: '70%' }}>
                                <Typography style={{ marginLeft: '15px' }} variant="subtitle2">
                                    {portInformation.email || '-'}
                                </Typography>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </Card>
        </>
    );
};

const MapPinInfo = ({ locationPin, locationPinOption, OnSeeMore }) => {
    const { instance, accounts } = useMsal();

    const [isLoading, setIsLoading] = useState(true);
    const [summaryData, setSummaryData] = useState();
    const [portInformation, setPortInformation] = useState();
    const [showPortInfo, setShowPortInfo] = useState(false);
    const portInformationButtonRef = useRef(null);
    const [positionTop, setPositionTop] = useState();
    const [positionLeft, setPositionLeft] = useState();

    useEffect(() => {
        (async () => {
            setIsLoading(true);
            if (locationPin && locationPinOption) {
                await getData();
            }
            setIsLoading(false);
        })();
    }, [locationPin, locationPinOption]);

    const getData = async () => {
        const authToken = await getAuthToken(instance, accounts);
        const apiResultType = getApiResultType(); // Get matching API result type.
        const summaryData = await RegulationService.getSummary(authToken, locationPin._id, apiResultType);
        setSummaryData(summaryData);

        if (locationPin._id && apiResultType === 'Port' && locationPin.country && locationPin.name) {
            const portInformation = await portService.getById(authToken, locationPin._id);
            setPortInformation(portInformation);
        } else {
            setPortInformation();
        }
    };

    // Function to get matching lookup data that API expects.
    const getApiResultType = () => {
        if (locationPinOption === Lookups.MapRegulatoryGroup.Region) return 'Region';
        if (locationPinOption === Lookups.MapRegulatoryGroup.Country) return 'Country';
        if (locationPinOption === Lookups.MapRegulatoryGroup.State) return 'State';
        if (locationPinOption === Lookups.MapRegulatoryGroup.Port) return 'Port';
    };

    useEffect(() => {
        window.addEventListener('resize', handleResize);
        handleResize();
        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, []);

    useEffect(() => {
        handleResize();
    }, [window.innerWidth, window.innerHeight, portInformationButtonRef.current?.getBoundingClientRect()]);

    //window.innerWidth, window.innerHeight,
    const handleResize = () => {
        const buttonRect = portInformationButtonRef.current?.getBoundingClientRect();
        if (buttonRect) {
            const top = buttonRect.top;
            const right = buttonRect.right;

            setPositionTop(window.innerHeight - top < 170 ? (window.innerHeight - top) / 2 : 170);
            setPositionLeft(window.innerWidth - right < 380 ? (window.innerWidth - right) / 2 : 380);
        }
    };

    return (
        <>
            {isLoading && <Typography>Loading...</Typography>}
            {!isLoading && summaryData && (
                <Card sx={{ width: '240px', boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.1)' }} elevation={0}>
                    <Grid container justifyContent="space-between" alignItems="left">
                        <Grid item xs={9}>
                            <Header locationPin={locationPin} locationPinOption={locationPinOption} />
                        </Grid>
                        <Grid item xs={3}>
                            {getApiResultType() === 'Port' && (
                                <Stack>
                                    <Button
                                        size="small"
                                        color="secondary"
                                        onClick={() => {
                                            setShowPortInfo(!showPortInfo);
                                        }}
                                        sx={{ backgroundColor: 'transparent', boxShadow: 'none', border: 'none', color: '#002A4D' }}
                                        ref={portInformationButtonRef}
                                    >
                                        <InfoIcon />
                                    </Button>
                                </Stack>
                            )}
                        </Grid>
                    </Grid>
                    <Content summaryData={summaryData} OnSeeMore={OnSeeMore} />
                </Card>
            )}

            <Popover
                open={showPortInfo}
                onClose={() => {
                    setShowPortInfo(!showPortInfo);
                }}
                anchorEl={portInformationButtonRef.current}
                anchorOrigin={{
                    vertical: 'middle',
                    horizontal: 'right'
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'left'
                }}
                slotProps={{
                    paper: {
                        sx: {
                            background: 'transparent',
                            boxShadow: 'none',
                            maxWidth: '80vh'
                        }
                    }
                }}
            >
                <Grid container alignItems={'flex-start'}>
                    <Grid item md={0.5} sm={1} xs={1.5}>
                        <ArrowLeftIcon />
                    </Grid>
                    <Grid item md={11.5} sm={11} xs={10.5}>
                        <PortInformationDisplay portInformation={portInformation} xlength={positionLeft} ylength={positionTop} />
                    </Grid>
                </Grid>
            </Popover>
        </>
    );
};

export default MapPinInfo;
